import React from "react";

/**
 * NeedsPhoto
 * ─────────────────────────────────────────────────────────────────────────
 * One answer to "is this cover a stand-in?", and the badge and filter chips
 * that put that answer on a catalogue screen. Every console that can receive
 * an imported catalogue reads this module; nobody re-derives the rule.
 *
 * Why it is shared rather than copied
 * -----------------------------------
 * The rule started life inside the brand console, where the catalogue
 * importer first shipped. Healthcare then grew its own importer, its own
 * `imageSource` column and its own review screen, and the shared review
 * screen (views/Components/CatalogueImportModal.js) tells the operator, for
 * BOTH verticals, that placeholder rows "will be marked Needs photo on the
 * product page, and you can filter for them there". The pharmacy workbook
 * template says the same thing in its own words. On healthcare that was a
 * promise to an operator with nothing behind it: the importer wrote
 * `imageSource`, the model stored it, and no screen in the pharmacy console
 * ever read it back, so 71 marked rows looked exactly like 71 photographed
 * ones and the marking bought nobody anything.
 *
 * Copying the predicate and the pill into the second console would have been
 * the smaller diff and the worse answer. The rule is a contract with the
 * importer, not a detail of one screen: the day PLACEHOLDER gains a second
 * spelling, or the day a third vertical gets an importer, a copy is a screen
 * that quietly disagrees with the review modal that promised the operator
 * something. One implementation, imported at every call site.
 *
 * What is and is not a stand-in
 * -----------------------------
 * `imageSource` is stamped by the importer when a row is brought in ahead of
 * its photography, so this is a recorded fact rather than a guess from the
 * URL. A product created by hand in the console has no `imageSource` at all,
 * and absence means "not imported", never "needs a photo".
 */

/* A product whose cover is a stand-in rather than real artwork. The importer
   stamps `imageSource` when a row was brought in ahead of its photography, so
   this is a recorded fact, not a guess from the URL. */
export const needsPhoto = (product) =>
  String(product?.imageSource || "")
    .toUpperCase()
    .startsWith("PLACEHOLDER");

/* What the badge says when an operator hovers it. Shared so the two consoles
   cannot drift into explaining the same mark two different ways. */
export const NEEDS_PHOTO_HINT =
  "This cover is a stand-in. Replace it with the real product photograph.";

/* The photo filters are ONE choice, not two switches.
   "Needs photo" began as a lone boolean. Adding "Has photo" as a second
   boolean would let an operator turn both on, which asks for products that
   simultaneously do and do not have artwork, which is always zero results,
   and the screen would say "Nothing matches" as though the catalogue were
   empty. A
   single key holding one of these keys, or null, cannot express that. */
export const PHOTO_FILTERS = [
  {
    key: "needsPhoto",
    label: "Needs photo",
    title: "Show only products still carrying a stand-in cover image",
    test: needsPhoto,
  },
  {
    key: "hasPhoto",
    label: "Has photo",
    title: "Show only products whose cover is real artwork",
    test: (product) => !needsPhoto(product),
  },
];

/* The chip counts, keyed the same way the filters are so a chip can read its
   own count by key. Counted over the WHOLE catalogue, never the filtered
   view: a count that shrinks when you press it is a count nobody can act on. */
export const photoTotals = (products) => {
  const all = Array.isArray(products) ? products : [];
  const needs = all.filter(needsPhoto).length;
  return { needsPhoto: needs, hasPhoto: all.length - needs };
};

/* Find the active filter's test, or undefined when nothing is on. */
export const photoFilterTest = (photoFilter) =>
  PHOTO_FILTERS.find((f) => f.key === photoFilter)?.test;

const BADGE_STYLE = {
  display: "inline-block",
  marginLeft: "8px",
  fontSize: "0.64rem",
  fontWeight: 700,
  letterSpacing: ".04em",
  background: "#fff4e5",
  color: "#a35b00",
  border: "1px solid #fcd9a4",
  padding: "1px 7px",
  borderRadius: "20px",
  verticalAlign: "1px",
};

/**
 * The marker itself. Renders nothing for a product that has real artwork, so
 * a caller can drop it beside a product name unconditionally.
 *
 * The amber pill is deliberately not one of Bootstrap's `Badge` variants: it
 * sits beside status badges that mean "this product is off" or "this drug is
 * controlled", and an outstanding photograph is a chore, not a state of the
 * product. It reads as a sticky note, and it reads the same on every console.
 */
export const NeedsPhotoBadge = ({ product, style }) =>
  needsPhoto(product) ? (
    <span style={{ ...BADGE_STYLE, ...(style || {}) }} title={NEEDS_PHOTO_HINT}>
      Needs photo
    </span>
  ) : null;

/* Izi's standard action colour. A chip that is ON is an action in effect. */
const CHIP_ON_BG = "#083860";

const chipStyle = (on) => ({
  fontSize: "0.76rem",
  fontWeight: 600,
  fontFamily: "'Inter', sans-serif",
  border: `1px solid ${on ? CHIP_ON_BG : "#dbe2ea"}`,
  background: on ? CHIP_ON_BG : "#fff",
  color: on ? "#fff" : "#475569",
  borderRadius: "8px",
  padding: "6px 11px",
  whiteSpace: "nowrap",
  transition: "all .18s ease",
});

/**
 * The two chips, as one control.
 *
 * `value` is the active key or null, `onChange` receives the next value, and
 * pressing the chip that is already on clears the filter. A chip whose count
 * is zero is disabled unless it is the one currently on, so an operator
 * cannot select an empty result and then wonder where the catalogue went.
 *
 * A console that already owns chip styling passes `className` and
 * `activeClassName` and gets its own look; the brand console does exactly
 * that, so this component could be introduced there without changing a pixel.
 * Everyone else gets the inline default, in the standard action colour.
 */
export const PhotoFilterChips = ({
  value,
  onChange,
  totals,
  className,
  activeClassName = "is-on",
}) => (
  <>
    {PHOTO_FILTERS.map((f) => {
      const on = value === f.key;
      const count = totals?.[f.key] ?? 0;
      return (
        <button
          key={f.key}
          type="button"
          className={
            className ? `${className}${on ? ` ${activeClassName}` : ""}` : undefined
          }
          style={className ? undefined : chipStyle(on)}
          onClick={() => onChange(on ? null : f.key)}
          disabled={!on && count === 0}
          aria-pressed={on}
          title={f.title}
        >
          {f.label} ({count})
        </button>
      );
    })}
  </>
);

export default NeedsPhotoBadge;
